import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Briefcase, MapPin, Phone, Mail, User, Calendar, FileCheck, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function CareersManager() {
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [showApplications, setShowApplications] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'CAREERS', hero_subtitle_bg: 'КАРИЕРИ', hero_subtitle_de: 'KARRIERE',
    hero_title_en: 'Join Our', hero_title_bg: 'Присъединете се към', hero_title_de: 'Werden Sie Teil unseres',
    hero_title_highlight_en: 'Team', hero_title_highlight_bg: 'екипа ни', hero_title_highlight_de: 'Teams',
    hero_description_en: 'Build your career with a leading transport company. We offer competitive benefits and growth opportunities.',
    hero_description_bg: 'Изградете кариерата си с водеща транспортна компания. Предлагаме конкурентни придобивки и възможности за растеж.',
    hero_description_de: 'Bauen Sie Ihre Karriere bei einem führenden Transportunternehmen auf. Wir bieten wettbewerbsfähige Leistungen und Wachstumsmöglichkeiten.',
    cta_title_en: 'Don\'t see a position?', cta_title_bg: 'Не виждате подходяща позиция?', cta_title_de: 'Keine passende Stelle gefunden?',
    cta_text_en: 'Send us your CV and we\'ll contact you when a suitable position opens.',
    cta_text_bg: 'Изпратете ни CV и ще се свържем с вас при подходяща позиция.',
    cta_text_de: 'Senden Sie uns Ihren Lebenslauf und wir melden uns bei passender Gelegenheit.',
    cta_button_en: 'Send CV', cta_button_bg: 'Изпрати CV', cta_button_de: 'Lebenslauf senden'
  });
  
  const defaultForm = {
    title_en: '', title_bg: '', title_de: '',
    department_en: '', department_bg: '', department_de: '',
    description_en: '', description_bg: '', description_de: '',
    requirements_en: '', requirements_bg: '', requirements_de: '',
    benefits_en: '', benefits_bg: '', benefits_de: '',
    location: '', contact_phone: '', contact_email: '',
    type: 'Full-time', order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchPositions(); fetchPageContent(); fetchApplications(); }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`);
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`);
      toast.success('Application deleted!');
      fetchApplications();
    } catch (error) {
      toast.error('Error deleting application');
    }
  };

  const handleToggleProcessed = async (app) => {
    try {
      const endpoint = app.is_processed ? 'unprocess' : 'process';
      await axios.put(`${API}/admin/applications/${app.id}/${endpoint}`);
      toast.success(app.is_processed ? 'Marked as pending' : 'Marked as processed');
      fetchApplications();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('bg-BG', { 
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/careers-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/careers-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/admin/careers`);
      setPositions(response.data.positions || []);
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        requirements_en: formData.requirements_en.split('\n').filter(r => r.trim()),
        requirements_bg: formData.requirements_bg.split('\n').filter(r => r.trim()),
        requirements_de: formData.requirements_de.split('\n').filter(r => r.trim()),
        benefits_en: formData.benefits_en.split('\n').filter(b => b.trim()),
        benefits_bg: formData.benefits_bg.split('\n').filter(b => b.trim()),
        benefits_de: formData.benefits_de.split('\n').filter(b => b.trim()),
        department_en: formData.department_en,
        department_bg: formData.department_bg,
        department_de: formData.department_de,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email
      };
      if (editingPosition) {
        await axios.put(`${API}/admin/careers/${editingPosition.id}`, submitData);
        toast.success('Position updated!');
      } else {
        await axios.post(`${API}/admin/careers`, submitData);
        toast.success('Position added!');
      }
      fetchPositions();
      setIsFormOpen(false);
      setEditingPosition(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving position');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      await axios.delete(`${API}/admin/careers/${id}`);
      toast.success('Position deleted!');
      fetchPositions();
    } catch (error) {
      toast.error('Error deleting position');
    }
  };

  const startEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      title_en: position.title_en || position.title || '',
      title_bg: position.title_bg || '',
      title_de: position.title_de || '',
      department_en: position.department_en || position.department || '',
      department_bg: position.department_bg || '',
      department_de: position.department_de || '',
      description_en: position.description_en || position.description || '',
      description_bg: position.description_bg || '',
      description_de: position.description_de || '',
      requirements_en: Array.isArray(position.requirements_en) ? position.requirements_en.join('\n') : '',
      requirements_bg: Array.isArray(position.requirements_bg) ? position.requirements_bg.join('\n') : '',
      requirements_de: Array.isArray(position.requirements_de) ? position.requirements_de.join('\n') : '',
      benefits_en: Array.isArray(position.benefits_en) ? position.benefits_en.join('\n') : '',
      benefits_bg: Array.isArray(position.benefits_bg) ? position.benefits_bg.join('\n') : '',
      benefits_de: Array.isArray(position.benefits_de) ? position.benefits_de.join('\n') : '',
      location: position.location || '',
      contact_phone: position.contact_phone || '',
      contact_email: position.contact_email || '',
      type: position.type || 'Full-time',
      order: position.order || 0,
      is_active: position.is_active !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updatePageField = (field, value) => setPageContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Applications Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowApplications(!showApplications)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Job Applications</span>
              {applications.length > 0 && (
                <span className="bg-[#C41E3A] text-white text-xs px-2 py-0.5 rounded-full">{applications.length}</span>
              )}
            </div>
            {showApplications ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showApplications && (
            <div className="mt-4 border-t pt-4 space-y-3">
              {applications.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No applications yet.</p>
              ) : (
                applications.map(app => (
                  <div key={app.id} className={`p-4 rounded-2xl transition-colors ${app.is_processed ? 'bg-green-50/50 border border-green-200' : 'bg-slate-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {/* Processed Checkbox */}
                        <button
                          onClick={() => handleToggleProcessed(app)}
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-1 ${
                            app.is_processed 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-slate-300 hover:border-green-400'
                          }`}
                        >
                          {app.is_processed && <Check className="w-4 h-4" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-slate-900">{app.name}</h4>
                            {app.is_processed && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Processed</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {app.email}</span>
                            {app.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {app.phone}</span>}
                          </div>
                          {app.position_id && (
                            <p className="text-sm text-[#C41E3A] mt-1">
                              Applied for: {positions.find(p => p.id === app.position_id)?.title_en || 'Unknown Position'}
                            </p>
                          )}
                          {app.cover_letter && (
                            <p className="text-sm text-slate-600 mt-2 bg-white p-2 rounded-lg">{app.cover_letter}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {formatDate(app.created_at)}
                          </p>
                          {app.processed_at && (
                            <p className="text-xs text-green-600 mt-1">Processed: {formatDate(app.processed_at)}</p>
                          )}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteApplication(app.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">Careers Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} Hero</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} placeholder="Subtitle" className="bg-white" />
                        <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} placeholder="Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} placeholder="Title Highlight" className="bg-white" />
                      <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} placeholder="Description" className="bg-white" rows={2} />
                      <h4 className={`font-bold ${lang.text} pt-2`}>{lang.flag} CTA</h4>
                      <Input value={pageContent[`cta_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_title_${lang.code}`, e.target.value)} placeholder="CTA Title" className="bg-white" />
                      <Textarea value={pageContent[`cta_text_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_text_${lang.code}`, e.target.value)} placeholder="CTA Text" className="bg-white" rows={2} />
                      <Input value={pageContent[`cta_button_${lang.code}`] || ''} onChange={(e) => updatePageField(`cta_button_${lang.code}`, e.target.value)} placeholder="CTA Button" className="bg-white" />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Open Positions</h2>
          <p className="text-sm text-slate-500">Job listings for the careers page</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingPosition(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Position
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingPosition ? 'Edit Position' : 'New Position'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input value={formData.location} onChange={(e) => updateField('location', e.target.value)} placeholder="Sofia, Bulgaria" className="mt-1" />
                </div>
                <div>
                  <Label>Type</Label>
                  <select value={formData.type} onChange={(e) => updateField('type', e.target.value)} className="mt-1 w-full h-10 rounded-md border px-3">
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                  </select>
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input value={formData.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} placeholder="+359 888 123 456" className="mt-1" />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input type="email" value={formData.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} placeholder="hr@tetralog.bg" className="mt-1" />
                </div>
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} />
                  <span>Active Position</span>
                </label>
              </div>

              {/* Language Tabs */}
              <Tabs value={activeLang} onValueChange={setActiveLang}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                      {lang.flag} {formData[`title_${lang.code}`] && '✓'}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Job Title {lang.code === 'en' && '*'}</Label>
                          <Input value={formData[`title_${lang.code}`]} onChange={(e) => updateField(`title_${lang.code}`, e.target.value)} className="mt-1 bg-white" required={lang.code === 'en'} placeholder={lang.code === 'en' ? 'e.g., International Truck Driver' : ''} />
                        </div>
                        <div>
                          <Label>Department</Label>
                          <Input value={formData[`department_${lang.code}`]} onChange={(e) => updateField(`department_${lang.code}`, e.target.value)} className="mt-1 bg-white" placeholder={lang.code === 'en' ? 'e.g., Logistics' : ''} />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={formData[`description_${lang.code}`]} onChange={(e) => updateField(`description_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={3} />
                      </div>
                      <div>
                        <Label>Requirements (one per line)</Label>
                        <Textarea value={formData[`requirements_${lang.code}`]} onChange={(e) => updateField(`requirements_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={4} />
                      </div>
                      <div>
                        <Label>Benefits (one per line)</Label>
                        <Textarea value={formData[`benefits_${lang.code}`]} onChange={(e) => updateField(`benefits_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={3} />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A]"><Save className="w-4 h-4 mr-2" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Positions List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {positions.map(position => (
          <Card key={position.id} className={`bg-white rounded-2xl shadow-sm ${!position.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold">{position.title_en || position.title}</h4>
                  {(position.department_en || position.department) && (
                    <p className="text-sm text-slate-600">{position.department_en || position.department}</p>
                  )}
                  <div className="flex gap-1 mt-1">
                    {LANGUAGES.map(lang => (
                      <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${position[`title_${lang.code}`] ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
                    {position.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {position.location}</span>}
                    <span>{position.type}</span>
                    {position.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {position.contact_phone}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => startEdit(position)} className="flex-1"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(position.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {positions.length === 0 && <p className="text-slate-500 text-center py-8 col-span-2">No positions yet.</p>}
      </div>
    </div>
  );
}
